/**
 * Subscription Tier Utilities
 *
 * Centralized logic for determining and managing subscription tiers
 */

import { logger } from './logger'

// Default permissions object (initial state)
export const DEFAULT_PERMISSIONS = {
  admin: false,
  advertiser: false,
  subscriptionStarter: false,
  subscriptionPro: false,
  subscriptionBusiness: false,
  subscriptionEnterprise: false
}

// Subscription tier constants
export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  STARTER: 'starter',
  PRO: 'pro',
  BUSINESS: 'business',
  ENTERPRISE: 'enterprise'
}

// Valid subscription statuses
export const SUBSCRIPTION_STATUSES = {
  ACTIVE: 'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  UNPAID: 'unpaid'
}

// Statuses that grant access
const VALID_STATUSES = [
  SUBSCRIPTION_STATUSES.ACTIVE,
  SUBSCRIPTION_STATUSES.TRIALING
]

/**
 * Check if a subscription is valid and grants access
 * @param {Object} subscription - Subscription object from Firestore
 * @returns {boolean}
 */
export function isValidSubscription(subscription) {
  if (!subscription) return false

  const hasValidStatus =
    subscription.status && VALID_STATUSES.includes(subscription.status)

  const hasItems =
    subscription.items?.length > 0 || subscription.items?.data?.length > 0

  return hasValidStatus && hasItems
}

/**
 * Extract subscription tier from various possible data structures
 * @param {Object} subscription - Subscription object from Firestore
 * @returns {string|null} - One of SUBSCRIPTION_TIERS or null if not found
 */
export function getSubscriptionTier(subscription) {
  if (!subscription) return null

  let tierString = null

  // Path 1: items[0].price.product.metadata.product (Firestore structure)
  if (subscription.items?.[0]?.price?.product?.metadata?.product) {
    tierString = subscription.items[0].price.product.metadata.product
  }
  // Path 2: items.data[0].price.product.metadata.product (Stripe API format)
  else if (subscription.items?.data?.[0]?.price?.product?.metadata?.product) {
    tierString = subscription.items.data[0].price.product.metadata.product
  }
  // Path 3: Check product name directly
  else if (subscription.items?.[0]?.price?.product?.name) {
    tierString = extractTierFromName(subscription.items[0].price.product.name)
  }
  // Path 4: Check plan name/nickname
  else if (subscription.items?.[0]?.plan?.nickname) {
    tierString = extractTierFromName(subscription.items[0].plan.nickname)
  }

  // Normalize to lowercase and validate against known tiers
  return normalizeTier(tierString)
}

/**
 * Extract tier from a product/plan name string
 * @param {string} name - Product or plan name
 * @returns {string|null}
 */
function extractTierFromName(name) {
  if (!name) return null

  const lowerName = name.toLowerCase()

  // Check in order of specificity (enterprise before starter, etc.)
  if (lowerName.includes('enterprise')) return SUBSCRIPTION_TIERS.ENTERPRISE
  if (lowerName.includes('business')) return SUBSCRIPTION_TIERS.BUSINESS
  if (lowerName.includes('pro')) return SUBSCRIPTION_TIERS.PRO
  if (lowerName.includes('starter')) return SUBSCRIPTION_TIERS.STARTER

  return null
}

/**
 * Normalize tier string to match SUBSCRIPTION_TIERS constants
 * @param {string} tier - Raw tier string
 * @returns {string|null}
 */
function normalizeTier(tier) {
  if (!tier) return null

  const normalized = tier.toLowerCase().trim()

  // Direct match
  if (Object.values(SUBSCRIPTION_TIERS).includes(normalized)) {
    return normalized
  }

  return null
}

/**
 * Get permissions object based on subscription tier
 * @param {string} tier - Subscription tier from SUBSCRIPTION_TIERS
 * @returns {Object} Permissions object with all subscription flags (only the active tier is true)
 */
export function getPermissionsForTier(tier) {
  // Start with all permissions false
  const permissions = {
    subscriptionStarter: false,
    subscriptionPro: false,
    subscriptionBusiness: false,
    subscriptionEnterprise: false
  }
  logger.debug('permissions', permissions)
  logger.debug('tier', tier)

  // Only set the relevant tier to true
  switch (tier) {
    case SUBSCRIPTION_TIERS.STARTER:
      permissions.subscriptionStarter = true
      break
    case SUBSCRIPTION_TIERS.PRO:
      permissions.subscriptionPro = true
      break
    case SUBSCRIPTION_TIERS.BUSINESS:
      permissions.subscriptionBusiness = true
      break
    case SUBSCRIPTION_TIERS.ENTERPRISE:
      permissions.subscriptionEnterprise = true
      break
    // FREE and default: all remain false (no need to set explicitly)
  }

  return permissions
}

/**
 * Get display name for a tier
 * @param {string} tier - Subscription tier from SUBSCRIPTION_TIERS
 * @returns {string}
 */
export function getTierDisplayName(tier) {
  const displayNames = {
    [SUBSCRIPTION_TIERS.FREE]: 'Free',
    [SUBSCRIPTION_TIERS.STARTER]: 'Starter',
    [SUBSCRIPTION_TIERS.PRO]: 'Pro',
    [SUBSCRIPTION_TIERS.BUSINESS]: 'Business',
    [SUBSCRIPTION_TIERS.ENTERPRISE]: 'Enterprise'
  }

  return displayNames[tier] || 'Unknown'
}

/**
 * Check if a tier has access to subscription features
 * @param {string} tier - Subscription tier from SUBSCRIPTION_TIERS
 * @returns {boolean}
 */
export function hasSubscriptionAccess(tier) {
  return tier && tier !== SUBSCRIPTION_TIERS.FREE
}

/**
 * Main function to process subscription and return permissions
 * Simple logic: Subscription in Firestore = source of truth
 * @param {Object} subscription - Subscription object from Firestore (or null/false for free users)
 * @returns {Object} Permissions object with subscription flags
 */
export function processSubscription(subscription) {
  // No subscription or invalid status = FREE tier
  if (!isValidSubscription(subscription)) {
    return getPermissionsForTier(SUBSCRIPTION_TIERS.FREE)
  }

  // Get tier from subscription metadata
  const tier = getSubscriptionTier(subscription)

  // If tier can't be determined, treat as FREE (don't grant access without proper metadata)
  if (!tier) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '⚠️ Active subscription found but tier could not be determined. Check Stripe product metadata.',
        subscription
      )
    }
    return getPermissionsForTier(SUBSCRIPTION_TIERS.FREE)
  }

  // Return permissions for the detected tier
  return getPermissionsForTier(tier)
}
